<template>
  <button
    v-tooltip="tooltipOptions"
    v-bind="rootProps"
    :aria-busy="isLoading"
    :aria-describedby="descriptionTextId"
    :aria-disabled="isDisabled || isLoading"
    :aria-label="label"
    :aria-pressed="isPressed"
    :class="activeClasses"
    :type="type"
    @click="handleClick"
  >
    <SbIcon v-if="icon" :name="icon" aria-hidden="true" />
    <SbLoading
      v-if="isLoading"
      type="spinner"
      size="normal"
      aria-hidden="true"
    />
    <slot />
    <slot name="pin" />
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { ButtonHTMLAttributes } from 'vue'
import SbIcon from '../Icon'
import type { IconName } from '../Icon'
import SbLoading from '../Loading'
import { Tooltip } from '../../directives'

import { reactiveOmit } from '@vueuse/core'
import type {
  SharedButtonAppearance,
  SharedButtonSize,
  SharedButtonType,
  SharedButtonVariant,
} from '../shared/button/types'

const vTooltip = Tooltip

export type IconButtonProps = {
  label: string
  appearance?: SharedButtonAppearance
  variant?: SharedButtonVariant
  size?: SharedButtonSize
  type?: SharedButtonType
  icon?: IconName
  /** Icon properties */
  /** Tooltip properties */
  hideTooltip?: boolean
  tooltipPosition?: string
  tooltipVariant?: string
  tooltipZindex?: number
  tooltipOffset?: number[]
  tooltipWithinTarget?: boolean
  /** Button properties */
  isDisabled?: boolean
  isLoading?: boolean
  isRounded?: boolean
  isPressed?: boolean | undefined
  descriptionTextId?: string
  // eslint-disable-next-line vue/prop-name-casing
} & /* @vue-ignore */ ButtonHTMLAttributes

const props = withDefaults(defineProps<IconButtonProps>(), {
  appearance: 'clear',
  variant: 'default',
  size: 'medium',
  type: 'submit',
  isDisabled: false,
  isLoading: false,
  isPressed: undefined,
  descriptionTextId: undefined,
  tooltipPosition: 'bottom',
  tooltipVariant: 'dark',
  isRounded: false,
  icon: undefined,
  tooltipZindex: undefined,
  tooltipWithinTarget: undefined,
})

const rootProps = reactiveOmit(props, [
  'label',
  'appearance',
  'variant',
  'size',
  'type',
  'icon',
  'hideTooltip',
  'tooltipPosition',
  'tooltipVariant',
  'tooltipZindex',
  'tooltipOffset',
  'isDisabled',
  'isLoading',
  'isRounded',
  'isPressed',
  'descriptionTextId',
  'tooltipWithinTarget',
])

defineSlots<{
  default: () => unknown
  pin: (props: {}) => unknown
}>()

const activeClasses = computed(() => {
  return [
    'sb-icon-button',
    `sb-icon-button--${props.variant}`,
    {
      'sb-icon-button--disabled': props.isDisabled,
      'sb-icon-button--small': props.size === 'small',
      'sb-icon-button--large': props.size === 'large',
      'sb-icon-button--loading': props.isLoading,
      'sb-icon-button--rounded': props.isRounded,
      'sb-icon-button--clear': props.appearance === 'clear',
    },
  ]
})

const tooltipOptions = computed(() => {
  return {
    label: props.label,
    position: props.tooltipPosition,
    variant: props.tooltipVariant,
    zIndex: props.tooltipZindex,
    show: !props.hideTooltip,
    withinTarget: props.tooltipWithinTarget,
    offset: props.tooltipOffset,
  }
})

const emit = defineEmits<{
  (e: 'click', event: MouseEvent): void
}>()

const handleClick = (event: MouseEvent): void => {
  if (!props.isDisabled && !props.isLoading) {
    emit('click', event)
  }
}
</script>

<style src="./icon-button.scss" lang="scss"></style>
